import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getStateCallbacks } from '@colyseus/sdk'
import { Message } from '../../../types/Messages'
import { addNotification } from '../stores/NotificationStore'

export const HAND_RAISED_SOUND_URL = '/sounds/screenSharingStopSharing.mp3'

/**
 * How long an unconfirmed request steers the toggle: comfortably longer than a
 * round trip. The server refuses a raise silently (rate limit, or the player is
 * not in a virtual room yet), so a refused raise never confirms; after this the
 * toggle follows the schema again instead of sending a lower nobody needs.
 */
export const PENDING_HAND_TTL_MS = 1500

export interface RaisedHands {
  /** Session ids with a raised hand. Its identity changes on every raise/lower, which is what re-renders readers of `room.state`. */
  handRaisedSessionIds: Set<string>
  localHandRaised: boolean
  toggleHandRaise: () => void
  setLocalHandRaised: (raised: boolean) => void
  /** Lowers someone else's hand. The server only honours it from the room owner or a presenter. */
  lowerHand: (sessionId: string) => void
}

export interface RaisedHandsOptions {
  /**
   * Whose raises get a chime and a toast. The schema's `players` view reaches
   * further than the conversation, so an office-wide listener must not announce
   * a hand raised across the map. Your own raise always chimes. Default: everyone.
   */
  shouldAnnounce?: (sessionId: string) => boolean
}

function playHandRaisedChime(): void {
  try {
    const audio = new Audio(HAND_RAISED_SOUND_URL)
    audio.play().catch((err) => console.warn('[useRaisedHands] Failed to play hand-raised sound:', err))
  } catch (e) {
    console.warn('[useRaisedHands] Error creating Audio object:', e)
  }
}

/**
 * Raise-hand state from `Player.handRaised` in the Colyseus schema. The office
 * runs one instance (`useOfficeRaisedHands`); presentation views share it.
 *
 * Only a hand going up while this hook is mounted is announced (chime, plus a
 * toast for other people's hands). Hands already up at mount, or up when their
 * player comes into this client's state view, are shown silently.
 */
export function useRaisedHands(room: any | null, options: RaisedHandsOptions = {}): RaisedHands {
  const dispatch = useDispatch()
  const [handRaisedSessionIds, setHandRaisedSessionIds] = useState<Set<string>>(() => new Set())
  // Read at announce time so a changing audience does not re-subscribe every listener.
  const shouldAnnounceRef = useRef(options.shouldAnnounce)
  shouldAnnounceRef.current = options.shouldAnnounce

  useEffect(() => {
    if (!room) {
      // Unbound (leave or kick): no hand from the old room may linger on a tile.
      setHandRaisedSessionIds((prev) => (prev.size === 0 ? prev : new Set()))
      return
    }

    const $ = getStateCallbacks(room) as any
    if (!$) return

    const setRaised = (sessionId: string, raised: boolean) => {
      setHandRaisedSessionIds((prev) => {
        if (prev.has(sessionId) === raised) return prev
        const next = new Set(prev)
        if (raised) next.add(sessionId)
        else next.delete(sessionId)
        return next
      })
    }

    // One listener per session. `players.onAdd` replays players that already
    // exist, so without this guard an existing player would be registered twice
    // and every raise would chime and toast twice.
    const listenerUnbinds = new Map<string, () => void>()
    // Last value seen per session. A player entering the state view arrives with
    // `handRaised` already set, and the decoder then fires the listener that
    // `onAdd` just registered with that same value; only a false → true change
    // is a new raise.
    const lastKnownRaised = new Map<string, boolean>()

    const watchPlayer = (player: any, sessionId: string) => {
      if (listenerUnbinds.has(sessionId)) return
      try {
        const unbind = $(player).listen(
          'handRaised',
          (value: boolean) => {
            const raised = !!value
            const wasRaised = lastKnownRaised.get(sessionId) ?? false
            lastKnownRaised.set(sessionId, raised)
            setRaised(sessionId, raised)
            if (!raised || wasRaised) return
            const isSelf = sessionId === room.sessionId
            if (!isSelf && shouldAnnounceRef.current && !shouldAnnounceRef.current(sessionId)) return
            playHandRaisedChime()
            if (isSelf) return
            dispatch(
              addNotification({
                type: 'info',
                message: `${player.name || sessionId} raised their hand ✋`,
                duration: 5000,
              })
            )
          },
          false
        )
        listenerUnbinds.set(sessionId, unbind)
      } catch (e) {
        console.warn('[useRaisedHands] Failed to listen to player handRaised:', e)
      }
    }

    const initialRaised = new Set<string>()
    room.state?.players?.forEach((player: any, sessionId: string) => {
      lastKnownRaised.set(sessionId, !!player?.handRaised)
      if (player?.handRaised) initialRaised.add(sessionId)
    })
    setHandRaisedSessionIds((prev) =>
      prev.size === initialRaised.size && [...prev].every((id) => initialRaised.has(id)) ? prev : initialRaised
    )

    const unbindOnAdd = $(room.state).players.onAdd((player: any, sessionId: string) => {
      lastKnownRaised.set(sessionId, !!player?.handRaised)
      if (player?.handRaised) setRaised(sessionId, true)
      watchPlayer(player, sessionId)
    })

    // Fires on disconnect and when a player leaves this client's state view.
    const unbindOnRemove = $(room.state).players.onRemove((_player: any, sessionId: string) => {
      const unbind = listenerUnbinds.get(sessionId)
      listenerUnbinds.delete(sessionId)
      lastKnownRaised.delete(sessionId)
      try {
        unbind?.()
      } catch {}
      setRaised(sessionId, false)
    })

    return () => {
      try {
        unbindOnAdd?.()
        unbindOnRemove?.()
      } catch {}
      listenerUnbinds.forEach((unbind) => {
        try {
          unbind()
        } catch {}
      })
      listenerUnbinds.clear()
    }
  }, [room, dispatch])

  const localHandRaised: boolean = room?.state?.players?.get(room.sessionId)?.handRaised ?? false

  // What was last asked for and not yet confirmed. `localHandRaised` only moves
  // when the server's change arrives, so two clicks inside one round trip would
  // otherwise both send the same value; a toggle flips the last request instead.
  // It expires, because a refused request never confirms.
  const pendingRaisedRef = useRef<boolean | null>(null)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearPending = useCallback(() => {
    pendingRaisedRef.current = null
    if (pendingTimerRef.current !== null) {
      clearTimeout(pendingTimerRef.current)
      pendingTimerRef.current = null
    }
  }, [])
  useEffect(() => {
    clearPending()
    return clearPending
  }, [localHandRaised, room, clearPending])

  const setLocalHandRaised = useCallback(
    (raised: boolean) => {
      if (!room) return
      clearPending()
      pendingRaisedRef.current = raised
      pendingTimerRef.current = setTimeout(clearPending, PENDING_HAND_TTL_MS)
      room.send(Message.RAISE_HAND, { raised })
    },
    [room, clearPending]
  )

  const toggleHandRaise = useCallback(() => {
    setLocalHandRaised(!(pendingRaisedRef.current ?? localHandRaised))
  }, [setLocalHandRaised, localHandRaised])

  const lowerHand = useCallback(
    (sessionId: string) => {
      room?.send(Message.RAISE_HAND, { raised: false, targetSessionId: sessionId })
    },
    [room]
  )

  return { handRaisedSessionIds, localHandRaised, toggleHandRaise, setLocalHandRaised, lowerHand }
}

export default useRaisedHands
